import { Mail, ShieldCheck } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { PortalHero } from '@/ui/PortalHero';

import { CAPA_PRIVACIDADE } from './imagensDeCapa';

/**
 * `/privacidade` — aviso de privacidade do portal.
 *
 * Existe por três motivos, e nenhum deles é enfeite: a faixa de cookies e o aviso
 * de tratamento de dados dos formulários apontam para cá (e link para rota
 * inexistente é tela quebrada); a seção 5 de docs/LGPD-E-ACESSIBILIDADE.md manda
 * publicar o canal do encarregado; e o critério E5.4 do PRD exige finalidade
 * declarada onde há coleta de dado pessoal.
 *
 * TODO conteúdo aqui sai de docs/LGPD-E-ACESSIBILIDADE.md. Nada foi inventado:
 * onde o documento não define — a lista de com quem os dados são compartilhados,
 * por exemplo — o texto diz que a Secretaria publica, em vez de preencher com
 * suposição.
 */
const CONTATO_ENCARREGADO = import.meta.env.VITE_CONTATO_ENCARREGADO?.trim();

const CATEGORIAS = [
  { categoria: 'Identificação', exemplos: 'CPF, CNPJ, nome, RG' },
  { categoria: 'Contato', exemplos: 'e-mail, telefone, endereço' },
  { categoria: 'Documental', exemplos: 'CNH, comprovantes, certidões' },
  { categoria: 'Financeira', exemplos: 'dados bancários, valores pagos' },
  { categoria: 'Acesso', exemplos: 'endereço de origem, navegador, histórico de entrada' },
  { categoria: 'Estudante', exemplos: 'nome e escola do estudante' },
];

const BASES_LEGAIS = [
  {
    tratamento: 'Credenciamento e execução do serviço de transporte',
    base: 'Execução de política pública (art. 7º, III, da LGPD)',
  },
  {
    tratamento: 'Fiscalização, multas e recursos',
    base: 'Cumprimento de obrigação legal (art. 7º, II)',
  },
  { tratamento: 'Pagamento e prestação de contas', base: 'Obrigação legal e regulatória' },
  { tratamento: 'Transporte do estudante', base: 'Execução de política pública de educação' },
  { tratamento: 'Comunicação não essencial', base: 'Consentimento (art. 7º, I)' },
];

const DIREITOS = [
  'Confirmação de que existe tratamento e acesso aos seus dados',
  'Correção de dado incompleto, inexato ou desatualizado',
  'Anonimização, bloqueio ou eliminação, observados os prazos legais de guarda',
  'Portabilidade, em formato aberto',
  'Informação sobre com quem os dados são compartilhados',
  'Revogação do consentimento, onde o tratamento se apoia nele',
];

const PROTECOES = [
  'Cada tela recebe apenas os campos que usa — não existe "trazer tudo e filtrar na tela".',
  'CPF aparece mascarado por padrão. Ver o número completo é ação deliberada e fica registrada.',
  'Consulta, exportação e impressão de dado pessoal geram registro que não pode ser alterado.',
  'Cada perfil de acesso enxerga apenas o seu escopo, e isso é garantido no banco de dados.',
  'Os dados são cifrados em trânsito e em repouso.',
  'Ambientes que não são de produção não recebem dado real de cidadão sem mascaramento.',
];

export function AvisoDePrivacidade() {
  return (
    <div>
      <PortalHero
        image={CAPA_PRIVACIDADE}
        title="Aviso de privacidade"
        description="Este aviso explica como o Sistema TEG trata dados pessoais. O controlador é a Prefeitura de São Paulo, por meio da Secretaria Municipal de Mobilidade Urbana e Transporte (SMT)."
        compact
      >
        <p className="mt-4 flex items-center gap-2 text-sm font-medium text-primary-foreground/85">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          Lei Geral de Proteção de Dados Pessoais — Lei nº 13.709/2018
        </p>
      </PortalHero>

      <div className="mx-auto max-w-3xl space-y-8 px-4 py-10 sm:px-6">
        <Secao titulo="Quais dados são tratados">
          <table className="w-full text-sm">
            <caption className="sr-only">
              Categorias de dado pessoal tratadas pelo sistema e exemplos de cada uma
            </caption>
            <thead>
              <tr className="border-b border-border text-left">
                <th scope="col" className="py-2 pr-4 font-semibold">
                  Categoria
                </th>
                <th scope="col" className="py-2 font-semibold">
                  Exemplos
                </th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIAS.map(({ categoria, exemplos }) => (
                <tr key={categoria} className="border-b border-border/60 last:border-0">
                  <th scope="row" className="py-2 pr-4 align-top font-medium">
                    {categoria}
                  </th>
                  <td className="py-2 align-top text-muted-foreground">{exemplos}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Dado de estudante é o mais sensível do sistema e tem regra mais rígida: acesso restrito
            por perfil, sempre registrado, e nunca exportado em massa sem autorização formal.
          </p>
        </Secao>

        <Secao titulo="Para que os dados são usados, e com que base legal">
          <ul className="space-y-3">
            {BASES_LEGAIS.map(({ tratamento, base }) => (
              <li key={tratamento} className="text-sm">
                <span className="font-medium">{tratamento}</span>
                <span className="block text-muted-foreground">{base}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm leading-relaxed text-muted-foreground">
            O consentimento é pedido apenas onde a base legal é o consentimento. Quando é o caso, o
            registro guarda a finalidade, a versão deste aviso, a data e a origem do pedido.
          </p>
        </Secao>

        <Secao titulo="Como os dados são protegidos">
          <ul className="space-y-2">
            {PROTECOES.map((protecao) => (
              <li key={protecao} className="text-sm leading-relaxed text-muted-foreground">
                {protecao}
              </li>
            ))}
          </ul>
        </Secao>

        <Secao titulo="Compartilhamento">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Os dados são compartilhados apenas quando a lei exige ou quando é necessário para a
            execução da política pública de transporte escolar — por exemplo, com órgãos de controle
            e com os sistemas municipais de pagamento. A relação específica de destinatários é
            publicada pela Secretaria Municipal de Mobilidade Urbana e Transporte (SMT) e pode ser
            solicitada ao encarregado.
          </p>
        </Secao>

        <Secao titulo="Seus direitos">
          <ul className="space-y-2">
            {DIREITOS.map((direito) => (
              <li key={direito} className="text-sm leading-relaxed text-muted-foreground">
                {direito}
              </li>
            ))}
          </ul>
          <p className="text-sm leading-relaxed text-muted-foreground">
            O prazo de resposta é de 15 dias, conforme a lei.
          </p>
        </Secao>

        <Secao titulo="Incidente de segurança">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Havendo suspeita de incidente com dado pessoal, o acesso é contido imediatamente, o caso
            é registrado e apurado em até 24 horas, e a Autoridade Nacional de Proteção de Dados e
            os titulares afetados são comunicados em até 2 dias úteis quando houver risco relevante.
          </p>
        </Secao>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Encarregado de proteção de dados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="leading-relaxed text-muted-foreground">
              Para exercer qualquer um dos direitos acima, fale com o encarregado de proteção de
              dados da Secretaria Municipal de Mobilidade Urbana e Transporte (SMT).
            </p>
            {CONTATO_ENCARREGADO ? (
              <p className="flex items-center gap-1.5">
                <Mail className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <a
                  href={`mailto:${CONTATO_ENCARREGADO}`}
                  className="text-link underline-offset-4 hover:underline"
                >
                  {CONTATO_ENCARREGADO}
                </a>
              </p>
            ) : (
              <p className="text-muted-foreground">
                O canal de atendimento ao titular é publicado pela Secretaria Municipal de
                Mobilidade Urbana e Transporte (SMT).
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold tracking-tight">{titulo}</h2>
      {children}
    </section>
  );
}

export default AvisoDePrivacidade;
